import React, {
  FC,
  KeyboardEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  Card,
  CardHeader,
  CardMedia,
  Modal,
  Slide,
} from '@material-ui/core';
import { CloseRounded, FileCopyOutlined, SendRounded } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { FileMessage, MessageType, setChatAlert } from 'models/twilio';
import { ApolloError, useMutation } from '@apollo/client';
import { GraphQLUploadFileInput } from 'network/graphql-models';
import uploadFileMutation from 'network/uploadFile';
import { APP_DOMAIN } from 'appenv';
import useCurrentAttendee from 'components/twilio/hooks/useCurrentAttendee';
import { BEHAVIOR, COLORS } from 'components/twilio/constants';
import useTwilioMessageSend from 'components/twilio/hooks/useTwilioMessageSend';
import getFileSizeText from 'components/chat/utils/getFileSizeText';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from 'components/twilio//translations';
import ColoredMaterialIcon, { ColoredMaterialIconProps } from './styled/ColoredMaterialIcon';
import { ChatSubtitleText, ChatTitleSubtitleGroupContainer, ChatTitleText } from './CommonChatDisplayComponents';
import ChatIconButton from './styled/ChatIconButton';
import ChatTextArea from './styled/ChatTextArea';
import ChatDivider from './styled/ChatDivider';
import isSupportedImage from '../utils/isSupportedImage';

const StyledCard = styled(Card)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  outline: none;
`;

const StyledCardHeader = styled(CardHeader)`
  background-color: ${COLORS.GREY_M};
  padding: 12px 12px 12px 16px;

  .MuiCardHeader-title {
    font-family: Lato, sans-serif;
    font-weight: bold;
    font-size: 16px;
    line-height: 22px;
    color: ${COLORS.TEXT_DARK};
  }
  .MuiCardHeader-action {
    margin: 0;
  }
`;

const CardPreviewContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledCardMedia = styled(CardMedia)`
  height: 80%;
  width: 80%;
  max-height: 80%;
  max-width: 80%;
  background-size: contain;
`;

const PreviewInput = styled.div`
  width: 100%;
  padding: 0 4px;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  background-color: ${COLORS.GREY_M};
`;

const FileDetail = styled(ChatTitleSubtitleGroupContainer)`
  padding: 8px 16px;
  width: 100%;
  display: flex;
  flex-grow: 0;
  justify-cnotent: center;
  align-items: center;
`;

const FileName = styled(ChatTitleText)`
  font-weight: normal;
  max-width: 100%;
`;

const FileSize = styled(ChatSubtitleText)`
  color: ${COLORS.LIGHT_GREY};
`;

const FilePreviewError = styled(ChatSubtitleText)`
  color: ${COLORS.ERROR};
`;

const StyledCircularLoadingIndicator = styled(CircularLoadingIndicator)`
  margin: 6px;
`;

const fileIconProps: ColoredMaterialIconProps = {
  Icon: FileCopyOutlined,
  color: COLORS.SKY_BLUE,
  overrides: {
    width: '56px',
    height: '56px',
  },
};

const sendIconProps: ColoredMaterialIconProps = {
  Icon: SendRounded,
  color: COLORS.WHITE,
  overrides: {
    paddingLeft: '2px',
  },
};

const StyledModal = styled(Modal)`
  position: absolute !important;
  width: 100%;
  height: 100%;
`;

const ColoredSendButton = styled(ChatIconButton)`
  background-color: ${COLORS.SKY_BLUE};
  :hover {
    background-color: ${COLORS.SKY_BLUE};
  }
`;

export interface FilePreviewProps {
  file: File;
  setFile: React.Dispatch<React.SetStateAction<File>>;
  fileInputRef: MutableRefObject<HTMLInputElement>;
  textAreaRef: MutableRefObject<HTMLTextAreaElement>;
}

const FilePreview: FC<FilePreviewProps> = ({
  file, setFile, fileInputRef, textAreaRef,
}) => {
  const { t } = useTranslation();
  const [hasError, setError] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { authToken } = useCurrentAttendee();
  const previewInputRef = useRef<HTMLTextAreaElement>();
  const { sendMessage } = useTwilioMessageSend();
  const isImage = useMemo(() => isSupportedImage(file), [file]);
  const [upload] = useMutation(uploadFileMutation, {
    onError: (e: ApolloError) => uploadErrorHandler(e.message),
    variables: {
      eventDomain: APP_DOMAIN,
      file,
      uniqueToken: authToken,
    } as GraphQLUploadFileInput,
  });

  const MediaView = useMemo(() => (isImage ? (
    <StyledCardMedia
      image={file && URL.createObjectURL(file)}
      title={file?.name}
    />
  ) : (
    <ColoredMaterialIcon {...fileIconProps} />
  )), [file, isImage]);

  const uploadErrorHandler = useCallback((message: string) => {
    setError(true);
    console.error(message);
    dispatch(setChatAlert({ message: TRANSLATIONS.ERRORS.UPLOAD_FAIL[1], translationKey: TRANSLATIONS.ERRORS.UPLOAD_FAIL[0], severity: 'error' }));
  }, [dispatch]);

  const resetTextAreas = useCallback(() => {
    previewInputRef.current.value = '';
    previewInputRef.current.setAttribute('style', '');
    textAreaRef.current.value = '';
    textAreaRef.current.setAttribute('style', '');
  }, [textAreaRef]);

  const resetFile = useCallback(() => {
    fileInputRef.current.value = null;
    setFile(null);
    setLoading(false);
    setError(false);
  }, [fileInputRef, setFile]);

  const onMessageSend = useCallback(async () => {
    if (file) {
      setLoading(true);
      const response = await upload();
      // eslint-disable-next-line prefer-destructuring
      const fileUrl = response?.data?.uploadFile?.attachment?.fileUrl;
      if (!fileUrl) {
        uploadErrorHandler('Backend did not return a file URL');
        setLoading(false);
        return;
      }
      const { name, size, type } = file;
      const uploadedFile: FileMessage = {
        name, size, type, url: fileUrl,
      };
      await sendMessage('[FILE]', {
        messageType: MessageType.File,
        file: uploadedFile,
      });
    }
    if (previewInputRef.current) {
      const message = previewInputRef.current.value;
      if (message.trim()) await sendMessage(message, {});
    }
    resetTextAreas();
    resetFile();
    setTimeout(() => textAreaRef.current.focus(), BEHAVIOR.CHAT_BACKDROP_TIMEOUT);
  }, [sendMessage, file, upload, textAreaRef, resetTextAreas, resetFile, uploadErrorHandler]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onMessageSend();
    }
    if (e.key === 'Escape') {
      resetFile();
    }
  }, [onMessageSend, resetFile]);

  useEffect(() => {
    if (file) {
      previewInputRef.current.value = textAreaRef.current.value;
      setTimeout(() => previewInputRef.current.focus(), BEHAVIOR.CHAT_BACKDROP_TIMEOUT);
    }
  }, [file, textAreaRef]);

  return (
    <StyledModal
      disablePortal
      disableEnforceFocus
      disableAutoFocus
      open={Boolean(file)}
      BackdropProps={{
        style: { position: 'absolute' },
        transitionDuration: BEHAVIOR.CHAT_BACKDROP_TIMEOUT,
      }}
    >
      <Slide in={Boolean(file)} direction="up" mountOnEnter unmountOnExit>
        <StyledCard square elevation={0}>
          <StyledCardHeader
            action={(
              <ChatIconButton onClick={resetFile}>
                <ColoredMaterialIcon Icon={CloseRounded} />
              </ChatIconButton>
            )}
            title={t(...TRANSLATIONS.COMMON.PREVIEW)}
          />
          <ChatDivider />
          <CardPreviewContainer>
            {MediaView}
            <FileDetail>
              <FileName>{file?.name}</FileName>
              {file?.size && <FileSize>{getFileSizeText(file.size)}</FileSize>}
              {hasError && <FilePreviewError>{t(...TRANSLATIONS.ERRORS.UPLOAD_ERROR)}</FilePreviewError>}
            </FileDetail>
          </CardPreviewContainer>
          <ChatDivider />
          <PreviewInput>
            <ChatTextArea
              ref={previewInputRef}
              autoComplete="off"
              onKeyDown={onKeyDown}
              rowsMax={2}
              disabled={isLoading}
            />
            {isLoading
              ? <StyledCircularLoadingIndicator size={24} />
              : (
                <ColoredSendButton onClick={onMessageSend}>
                  <ColoredMaterialIcon {...sendIconProps} />
                </ColoredSendButton>
              )}
          </PreviewInput>
        </StyledCard>
      </Slide>
    </StyledModal>
  );
};

export default FilePreview;
